<script lang="ts">
  import FileTextIcon from "@lucide/svelte/icons/file-text";
  import Emoji from "$lib/components/Emoji.svelte";
  import type { PageSearchResult } from "$lib/core/types.js";
  import { highlightParts } from "$lib/search/global-search.js";
  import { cn, formatMessageTimestamp } from "$lib/utils.js";

  // The enriched page row of the top search bar (CYBORG-1240). Modeled on TaskSearchResults: same row
  // padding, same XSS-safe highlight (parts render as text nodes, never {@html}), same deep-link
  // as the page tree (/tasks/<project>/pages/<id>). Rows only: the section label, "Show N more" and the failure
  // state belong to the caller, like tasks. `startIndex`/`activeIndex` let the caller's arrow-key
  // navigation span every section (rows carry data-index).
  let {
    results,
    query,
    workspaceId,
    startIndex = 0,
    activeIndex = -1,
    onhover,
    onselect,
  }: {
    results: PageSearchResult[];
    query: string;
    workspaceId: string;
    startIndex?: number;
    activeIndex?: number;
    onhover?: (index: number) => void;
    onselect?: () => void;
  } = $props();
</script>

{#snippet marked(text: string)}
  {@const parts = highlightParts(text, query)}
  {parts[0]}{#if parts[1]}<mark class="rounded-sm bg-warning/30 px-0.5 text-content">{parts[1]}</mark>{/if}{parts[2]}
{/snippet}

{#each results as r, i (r.id)}
  {@const idx = startIndex + i}
  <a
    href={`/workspace/${workspaceId}/tasks/${r.projectId}/pages/${r.id}`}
    data-index={idx}
    data-result-kind="page"
    onclick={() => onselect?.()}
    onmousemove={() => onhover?.(idx)}
    class={cn(
      "block w-full border-b border-edge px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-raised",
      activeIndex === idx && "bg-raised",
    )}
  >
    <div class="flex items-start gap-2.5">
      <span class="flex size-7 shrink-0 items-center justify-center rounded-md bg-raised text-content-dim">
        {#if r.icon}
          <Emoji emoji={r.icon} size={16} />
        {:else}
          <FileTextIcon size={16} strokeWidth={1.75} aria-hidden="true" />
        {/if}
      </span>
      <div class="min-w-0 flex-1">
        <div class="mb-1 flex min-w-0 items-center gap-2 text-xs text-content-dim">
          {#if r.project}
            <span class="flex min-w-0 items-center gap-1">
              {#if r.project.color}
                <span class="size-2.5 shrink-0 rounded-full" style={`background-color:${r.project.color}`}></span>
              {/if}
              <span class="truncate">{r.project.name}</span>
            </span>
          {/if}
          {#if r.project && r.editor}
            <span class="text-content-muted">·</span>
          {/if}
          <!-- No editor (deleted or unknown account) renders nothing: never the raw id. -->
          {#if r.editor}
            <span class="flex min-w-0 items-center gap-1">
              <span class="truncate">{r.editor.edited ? "Edited by" : "By"} {r.editor.name}</span>
              {#if r.editor.isAgent}
                <span class="shrink-0 rounded bg-accent/15 px-1 text-micro font-medium text-accent">Agent</span>
              {/if}
            </span>
          {/if}
          {#if r.updatedAt != null}
            <span class="ml-auto shrink-0 text-content-muted">{formatMessageTimestamp(r.updatedAt)}</span>
          {/if}
        </div>
        <div class="line-clamp-2 text-sm font-semibold text-content">
          {@render marked(r.title || "Untitled")}
        </div>
        {#if r.snippet && r.snippet.trim().length > 0}
          <div class="mt-0.5 line-clamp-2 text-sm text-content-dim">
            {@render marked(r.snippet)}
          </div>
        {/if}
      </div>
    </div>
  </a>
{/each}
